/**
 * [Gurumi 링크 모달]
 * 역할: yesmoney EditorLinkPreviewModal 포팅 (🔍 OG 미리보기)
 * 작성/수정일: 2026-07-29
 */
import { iconSvg } from '../icons.js';
import { fetchLinkPreview } from '../api.js';
import { resolveLinkPreviewImageUrl } from '../extensions/link-preview.js';

function parseHttpUrl(input) {
  try {
    const url = new URL(input.trim());
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    return url.toString();
  } catch {
    return null;
  }
}

function renderPreviewCard(preview) {
  const imageUrl = resolveLinkPreviewImageUrl(preview.image);
  const title = (preview.title || '').trim() || '링크 미리보기';
  return `
    <div class="editor-link-preview g5-gurumi-modal-preview">
      <div class="editor-link-preview__card pointer-events-none">
        ${imageUrl ? `<img class="editor-link-preview__image" src="${imageUrl.replace(/"/g, '&quot;')}" alt="" loading="lazy" />` : ''}
        <div class="editor-link-preview__body">
          <div class="editor-link-preview__title">${title.replace(/</g, '&lt;')}</div>
          ${preview.description?.trim() ? `<div class="editor-link-preview__desc">${preview.description.replace(/</g, '&lt;')}</div>` : ''}
          ${preview.domain?.trim() ? `<div class="editor-link-preview__domain">${preview.domain.replace(/</g, '&lt;')}</div>` : ''}
        </div>
      </div>
    </div>`;
}

export function openLinkPreviewModal({ initialUrl = '', cardMode = true, config, onInsertTextLink, onInsertPreview, onClose }) {
  const overlay = document.createElement('div');
  overlay.className = 'g5-gurumi-modal-root';

  const backdrop = document.createElement('button');
  backdrop.type = 'button';
  backdrop.className = 'g5-gurumi-modal-backdrop';
  backdrop.setAttribute('aria-label', '닫기');

  const dialog = document.createElement('div');
  dialog.className = 'g5-gurumi-modal-dialog g5-gurumi-modal-dialog-lg';
  dialog.setAttribute('role', 'dialog');
  dialog.setAttribute('aria-modal', 'true');

  const head = document.createElement('div');
  head.className = 'g5-gurumi-modal-head g5-gurumi-modal-head-center';
  head.innerHTML = '<h2 class="g5-gurumi-modal-title">링크</h2>';
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'g5-gurumi-modal-close g5-gurumi-modal-close-abs';
  closeBtn.appendChild(iconSvg('x'));
  head.appendChild(closeBtn);

  const body = document.createElement('div');
  body.className = 'g5-gurumi-modal-body';

  const urlWrap = document.createElement('div');
  urlWrap.className = 'g5-gurumi-link-input-wrap';
  const input = document.createElement('input');
  // [링크 모달] type=url 브라우저 제약으로 확인 클릭이 막히는 경우 방지 — 작성/수정일: 2026-07-29
  input.type = 'text';
  input.inputMode = 'url';
  input.autocomplete = 'off';
  input.spellcheck = false;
  input.className = 'g5-gurumi-link-input';
  input.placeholder = 'https://example.com';
  input.value = initialUrl;
  urlWrap.appendChild(input);

  let searchBtn = null;
  if (cardMode) {
    searchBtn = document.createElement('button');
    searchBtn.type = 'button';
    searchBtn.className = 'g5-gurumi-link-search';
    searchBtn.setAttribute('aria-label', '미리보기 조회');
    searchBtn.appendChild(iconSvg('search'));
    urlWrap.appendChild(searchBtn);
  }

  const hint = document.createElement('p');
  hint.className = 'g5-gurumi-modal-hint';
  hint.textContent = cardMode
    ? '🔍으로 미리보기를 조회하면 카드로 삽입됩니다. 조회하지 않거나 실패하면 URL 링크로 삽입됩니다.'
    : '선택한 텍스트에 링크가 연결됩니다.';

  const previewBox = document.createElement('div');
  previewBox.className = 'g5-gurumi-link-preview-box';

  const errorEl = document.createElement('p');
  errorEl.className = 'g5-gurumi-modal-error';
  errorEl.hidden = true;

  const confirmWrap = document.createElement('div');
  confirmWrap.className = 'g5-gurumi-modal-confirm-wrap';
  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'g5-gurumi-btn g5-gurumi-btn-primary g5-gurumi-btn-confirm';
  confirm.innerHTML = '<span>확인</span>';
  confirm.prepend(iconSvg('check'));
  confirmWrap.appendChild(confirm);

  body.appendChild(urlWrap);
  body.appendChild(hint);
  body.appendChild(previewBox);
  body.appendChild(errorEl);
  body.appendChild(confirmWrap);

  dialog.appendChild(head);
  dialog.appendChild(body);
  overlay.appendChild(backdrop);
  overlay.appendChild(dialog);
  document.body.appendChild(overlay);
  document.body.style.overflow = 'hidden';

  let preview = null;
  let previewUrl = null;
  let loading = false;

  function close() {
    document.body.style.overflow = '';
    overlay.remove();
    onClose?.();
  }

  function setLoading(on) {
    loading = on;
    if (searchBtn) {
      searchBtn.disabled = on || !parseHttpUrl(input.value);
      searchBtn.innerHTML = '';
      searchBtn.appendChild(iconSvg(on ? 'loader' : 'search'));
    }
    confirm.disabled = on || !parseHttpUrl(input.value);
  }

  function refreshConfirm() {
    confirm.disabled = loading || !parseHttpUrl(input.value);
  }

  async function fetchPreview() {
    const parsed = parseHttpUrl(input.value);
    if (!parsed) {
      errorEl.hidden = false;
      errorEl.textContent = '올바른 HTTP(S) 링크를 입력하세요.';
      previewBox.innerHTML = '';
      preview = null;
      previewUrl = null;
      return;
    }
    setLoading(true);
    errorEl.hidden = true;
    try {
      preview = await fetchLinkPreview(parsed, config);
      previewUrl = parsed;
      previewBox.innerHTML = renderPreviewCard(preview);
    } catch (err) {
      preview = null;
      previewUrl = parsed;
      previewBox.innerHTML = '';
      errorEl.hidden = false;
      errorEl.textContent = `${err.message || '링크 미리보기를 가져오지 못했습니다.'} 확인을 누르면 URL 링크로 삽입됩니다.`;
    } finally {
      setLoading(false);
      refreshConfirm();
    }
  }

  input.addEventListener('input', () => {
    errorEl.hidden = true;
    if (previewUrl && parseHttpUrl(input.value) !== previewUrl) {
      preview = null;
      previewUrl = null;
      previewBox.innerHTML = '';
    }
    refreshConfirm();
    if (searchBtn) searchBtn.disabled = loading || !parseHttpUrl(input.value);
  });

  searchBtn?.addEventListener('click', () => void fetchPreview());

  confirm.addEventListener('click', async () => {
    const parsed = parseHttpUrl(input.value);
    if (!parsed || loading) return;
    close();
    if (cardMode && preview && previewUrl === parsed) {
      await onInsertPreview({
        href: preview.url || parsed,
        title: preview.title,
        description: preview.description,
        image: preview.image,
        domain: preview.domain,
      });
    } else {
      onInsertTextLink(parsed);
    }
  });

  backdrop.addEventListener('click', close);
  closeBtn.addEventListener('click', close);
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && parseHttpUrl(input.value) && !loading) confirm.click();
    if (e.key === 'Escape') close();
  });

  setTimeout(() => {
    input.focus();
    input.select();
  }, 30);
  refreshConfirm();

  return { close };
}
